import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './FacilityTable.module.scss';
import { DeleteBin6LineIcon, EditLineIcon, Information2LineIcon, SortAscIcon, SortDescIcon } from '~/assets/icons';
import { IFacility } from '~/utils/interface/facility';
import { ChangeEvent, useRef } from 'react';
import { SEARCH_TIMEOUT } from '~/utils/constants/common';

type FacilityProps = {
  pageSize: number;
  data: IFacility[];
  form: any;
  setRefreshData?: (refresh: boolean) => void;
  onDetail?: (row: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  facilityIds?: Array<string>;
  handleCheck?: (row: IFacility, checked: boolean) => void;
  handleCheckAll?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
};
const cx = classNames.bind(styles);

const FacilityTable = (props: FacilityProps) => {
  const { form, facilityIds, handleCheck, handleCheckAll } = props;

  const { t } = useTranslation('admin/facility');

  const searchTimeout = useRef<any>(null);

  const getStatusStyle = (value: any) => {
    if (Number(value) === 0) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] py-[3px] px-[15px]'>
          {t('statusOptions.pending')}
        </Box>
      );
    } else if (Number(value) === 1) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] py-[3px] px-[15px]'>
          {t('statusOptions.deployed')}
        </Box>
      );
    } else if (Number(value) === 2) {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px] py-[3px] px-[15px]'>
          {t('statusOptions.activated')}
        </Box>
      );
    }
  };

  const handleSelectedOptions = (field: string, value: any) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      props.form.setFieldValue(field, value);
    }, SEARCH_TIMEOUT);
  };

  const getPeriodicCheckStatusStyle = (value: any) => {
    if (Number(value) === 0) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.noDevice')}
        </Box>
      );
    } else if (Number(value) === 1) {
      return (
        <Box className='text-[#F32A4C] border border-[#F32A4C] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.overDate')}
        </Box>
      );
    } else if (Number(value) === 3) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.dueDate')}
        </Box>
      );
    } else {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.valid')}
        </Box>
      );
    }
  };

  const handleSort = (field: string, type: string) => {
    form.setFieldValue('order_by', { field, type });
  };

  const formatLocationCode = (rawCode: string, province_code: string, ward_code: string) => {
    if (!rawCode || !province_code) return rawCode;

    if (rawCode.includes('_')) return rawCode;

    let result = '';
    let remaining = rawCode;

    if (remaining.startsWith(province_code)) {
      result += province_code;
      remaining = remaining.slice(province_code.length);
    } else {
      result += province_code;
    }

    if (ward_code && remaining.startsWith(ward_code)) {
      remaining = remaining.slice(ward_code.length);
      result += `_${ward_code}_${remaining}`;
    } else {
      result += `_${remaining}`;
    }

    return result;
  };

  return (
    <TableContainer
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: '1px',
      }}
    >
      <Table stickyHeader>
        <TableHead className='bg-[#005F73]'>
          {' '}
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                sx={{
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
                onChange={handleCheckAll}
              />
            </TableCell>

            <TableCell className={cx('min-w-[70px]', 'table-header')} align='center'>
              {t('no')}
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[191px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('locationCode')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'location_code' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('location_code', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('location_code', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.location_code}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('location_code', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('locationName')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'location_name' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('location_name', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('location_name', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.location_name}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('location_name', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[126px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('province')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'province' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('province', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('province', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.province}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('province', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[136px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('ward')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'ward' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('ward', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('ward', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.ward}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('ward', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[164px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('address')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'address' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('address', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('address', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.address}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('address', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('managerPhone')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'manager_phone' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('manager_phone', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('manager_phone', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.manager_phone}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('manager_phone', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[159px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('managerUnit')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'manager_unit' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('manager_unit', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('manager_unit', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.manager_unit}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('manager_unit', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[180px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('status')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[193px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('periodicCheck')}</Box>
                {form.values.order_by.type === 'asc' && form.values.order_by.field === 'periodic_check_status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('periodic_check_status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('periodic_check_status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell className={cx('w-[150px]', 'table-header')} align='center'>
              {t('action')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.data &&
            props.data.map((row: any, index: any) => (
              <TableRow key={index}>
                <TableCell padding='checkbox'>
                  <Checkbox
                    checked={(facilityIds ?? []).find((id) => id === row.location_id) ? true : false}
                    onChange={(event, checked) => handleCheck && handleCheck(row, checked)}
                    sx={{
                      color: '#005F73',
                      '&.Mui-checked': {
                        color: '#005F73',
                      },
                    }}
                  />
                </TableCell>
                <TableCell align='center'>{index + 1 + (form.values.page_index - 1) * props.pageSize}</TableCell>
                <TableCell align='center'>
                  {formatLocationCode(row.location_code, row.province_code, row.ward_code)}
                </TableCell>
                <TableCell align='center'>{row.location_name}</TableCell>
                <TableCell align='center'>{row.province}</TableCell>
                <TableCell align='center'>{row.ward}</TableCell>
                <TableCell align='center'>{row.address}</TableCell>
                <TableCell align='center'>{row.manager_phone}</TableCell>
                <TableCell align='center'>{row.manager_unit}</TableCell>
                <TableCell align='center'>{getStatusStyle(row.status)}</TableCell>
                <TableCell align='center'>{getPeriodicCheckStatusStyle(row.periodic_check_status)}</TableCell>
                <TableCell align='center'>
                  <Box className='flex'>
                    <IconButton onClick={() => props?.onDetail && props.onDetail(row)}>
                      <Information2LineIcon fill='#72777B' />
                    </IconButton>
                    <IconButton onClick={() => props?.onEdit && props.onEdit(row)}>
                      <EditLineIcon />
                    </IconButton>
                    <IconButton onClick={() => props?.onDelete && props.onDelete(row)}>
                      <DeleteBin6LineIcon fill='#ED1002' />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FacilityTable;
